import * as React from "react";

import { CitationUnderline } from "../../../components/CitationUnderline";
import { SimpleMarkdown } from "../../../components/SimpleMarkdown";
import { parseUnderlineTags } from "../../ai/parseUnderlineTags";
import { isMarkdownSeparatorRow, splitMarkdownTableRow } from "../../ai/tableHelpers";
import type { TableSourceItem } from "../../ai/sourceItems";
import {
  SOURCE_TABLE_CLASS_NAME,
  SOURCE_TD_CLASS_NAME,
  SOURCE_TH_CLASS_NAME,
} from "./sourceTableClasses";

const DEFAULT_FIRST_COLUMN_MIN_WIDTH = "148px";
const DEFAULT_COLUMN_MIN_WIDTH = "96px";
const COLUMN_MIN_WIDTH_OVERRIDES: Record<string, string> = {
  "business summary": "360px",
  "company name": "148px",
  currency: "76px",
  description: "220px",
  "filing date": "112px",
  "fiscal period end date": "132px",
  metric: "104px",
  "metric description": "156px",
  periodicity: "88px",
};

export const TableSourceRenderer: React.FC<{
  item: TableSourceItem;
  highlightUuid: string | null;
}> = ({ item, highlightUuid }) => {
  const table = React.useMemo(() => parseFactSetMarkdownTable(item.markdown), [item.markdown]);
  const scrollContainerRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    if (!table || !highlightUuid) return undefined;

    let frameId = 0;
    let attempts = 0;
    const maxAttempts = 24;

    const scrollToMarker = () => {
      const container = scrollContainerRef.current;
      const target = container?.querySelector<HTMLElement>(".factset-table-active-citation");
      if (!target) {
        attempts += 1;
        if (attempts < maxAttempts) {
          frameId = window.requestAnimationFrame(scrollToMarker);
        }
        return;
      }
      target.scrollIntoView({ behavior: "smooth", block: "center", inline: "center" });
    };

    frameId = window.requestAnimationFrame(scrollToMarker);

    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, [highlightUuid, table]);

  if (!table) {
    return (
      <div className="rounded-lg border border-border bg-background px-3 py-2">
        <SimpleMarkdown
          text={item.markdown}
          className="citation-markdown-content"
          tableClassName={SOURCE_TABLE_CLASS_NAME}
          thClassName={SOURCE_TH_CLASS_NAME}
          tdClassName={SOURCE_TD_CLASS_NAME}
          activeCitationId={highlightUuid}
        />
      </div>
    );
  }

  return (
    <section className="flex min-h-0 flex-1 flex-col overflow-hidden bg-background">
      <div className="flex min-h-0 w-full min-w-0 flex-1 flex-col bg-background px-0 pt-1.5">
        {table.title && (
          <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-b border-border/60 pb-2">
            <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
              {table.title}
            </p>
          </div>
        )}

        <div className="mt-2 min-h-0 flex-1 overflow-hidden rounded-md border border-border bg-background shadow-[inset_0_1px_0_rgba(255,255,255,0.45)]">
          <div ref={scrollContainerRef} className="factset-scrollbar h-full overflow-auto">
            <table className="min-w-full table-auto border-separate border-spacing-0 text-left text-[10.5px] font-headings">
              <thead>
                <tr>
                  {table.headers.map((cell, index) => {
                    const columnStyle = getColumnWidthStyle(table.headers, index);
                    return (
                      <th
                        key={`factset-header-${index}`}
                        className={getHeaderCellClassName(table.headers, index)}
                        style={columnStyle}
                      >
                        {renderFactSetCellContent(cell, highlightUuid)}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {table.rows.map((row, rowIndex) => (
                  <tr
                    key={`factset-row-${rowIndex}`}
                    className="group transition-colors odd:bg-muted/[0.16] hover:bg-muted/35"
                  >
                    {row.map((cell, cellIndex) => {
                      const columnStyle = getColumnWidthStyle(table.headers, cellIndex);
                      return (
                        <td
                          key={`factset-cell-${rowIndex}-${cellIndex}`}
                          className={getBodyCellClassName(
                            table.headers,
                            cellIndex,
                            rowIndex === table.rows.length - 1
                          )}
                          style={columnStyle}
                        >
                          {renderFactSetCellContent(cell, highlightUuid)}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </section>
  );
};

interface ParsedFactSetTable {
  title: string;
  headers: string[];
  rows: string[][];
}

function parseFactSetMarkdownTable(markdown: string): ParsedFactSetTable | null {
  const lines = markdown.split(/\r?\n/);
  const tableStart = lines.findIndex((line) => line.trim().startsWith("|"));
  if (tableStart < 0) return null;

  const title = lines
    .slice(0, tableStart)
    .map((line) => line.replace(/^#+\s*/, "").trim())
    .filter(Boolean)[0] ?? "";
  const tableLines: string[] = [];
  for (let i = tableStart; i < lines.length; i++) {
    const trimmed = lines[i].trim();
    if (!trimmed.startsWith("|")) {
      if (trimmed) break;
      continue;
    }
    tableLines.push(trimmed);
  }
  if (tableLines.length < 2) return null;

  const headers = splitMarkdownTableRow(tableLines[0]);
  if (headers.length === 0) return null;
  const dataStart = isMarkdownSeparatorRow(tableLines[1]) ? 2 : 1;
  const rows = tableLines
    .slice(dataStart)
    .filter((line) => !isMarkdownSeparatorRow(line))
    .map((line) => normalizeMarkdownRow(splitMarkdownTableRow(line), headers.length));

  return { title, headers, rows };
}

function normalizeMarkdownRow(cells: string[], expectedLength: number): string[] {
  if (cells.length === expectedLength) return cells;
  if (cells.length > expectedLength) {
    return [
      ...cells.slice(0, expectedLength - 1),
      cells.slice(expectedLength - 1).join(" | "),
    ];
  }
  return [...cells, ...new Array(expectedLength - cells.length).fill("")];
}

function getColumnWidthStyle(
  headers: string[],
  columnIndex: number
): React.CSSProperties {
  const header = headers[columnIndex] ?? "";
  const minWidth =
    COLUMN_MIN_WIDTH_OVERRIDES[normalizeHeaderName(header)] ??
    (columnIndex === 0 ? DEFAULT_FIRST_COLUMN_MIN_WIDTH : DEFAULT_COLUMN_MIN_WIDTH);
  return { minWidth };
}

function getHeaderCellClassName(headers: string[], columnIndex: number): string {
  const header = headers[columnIndex] ?? "";
  return [
    "sticky top-0 border-b border-r border-border px-2.5 py-2",
    "shadow-[0_1px_0_rgba(0,0,0,0.05),0_6px_12px_rgba(15,23,42,0.035)]",
    "text-[9px] font-semibold uppercase leading-tight tracking-[0.08em] text-muted-foreground",
    getColumnAlignmentClassName(header),
    getColumnWhitespaceClassName(header),
    columnIndex === 0
      ? "left-0 z-30 bg-muted shadow-[1px_0_0_rgba(0,0,0,0.08),0_1px_0_rgba(0,0,0,0.05),6px_0_12px_rgba(15,23,42,0.035)]"
      : "z-20 bg-muted",
  ].join(" ");
}

function getBodyCellClassName(
  headers: string[],
  columnIndex: number,
  isLastRow: boolean
): string {
  const header = headers[columnIndex] ?? "";
  return [
    "border-b border-r border-border/45 px-2.5 py-1.5 align-top leading-snug",
    "transition-colors",
    isLastRow ? "border-b-0" : "",
    columnIndex === 0
      ? "sticky left-0 z-10 bg-background font-semibold text-foreground shadow-[1px_0_0_rgba(0,0,0,0.08),6px_0_12px_rgba(15,23,42,0.025)] group-hover:bg-muted"
      : "text-muted-foreground group-hover:bg-muted/35",
    getColumnToneClassName(header),
    getColumnAlignmentClassName(header),
    getColumnWhitespaceClassName(header),
  ].join(" ");
}

function getColumnAlignmentClassName(header: string): string {
  const normalized = normalizeHeaderName(header);
  if (
    /\b(value|revenue|income|market cap|shares outstanding|employees|price|eps|ebitda|margin)\b/.test(
      normalized
    )
  ) {
    return "text-right tabular-nums";
  }
  if (
    /\b(currency|periodicity|fiscal period|fiscal period end date|filing date|date)\b/.test(
      normalized
    )
  ) {
    return "text-center tabular-nums";
  }
  return "text-left";
}

function getColumnWhitespaceClassName(header: string): string {
  const normalized = normalizeHeaderName(header);
  if (/\b(summary|description|business|website)\b/.test(normalized)) {
    return "whitespace-normal";
  }
  return "whitespace-nowrap";
}

function getColumnToneClassName(header: string): string {
  const normalized = normalizeHeaderName(header);
  if (/\b(metric|ticker|currency|periodicity)\b/.test(normalized)) {
    return "font-mono text-[10px] font-medium uppercase tracking-[0.02em] text-foreground/80";
  }
  if (/\b(value|revenue|income|market cap|shares outstanding|employees|price|eps|ebitda)\b/.test(normalized)) {
    return "font-mono font-semibold text-foreground";
  }
  return "";
}

function normalizeHeaderName(header: string): string {
  return header.replace(/<[^>]+>/g, "").replace(/\s+/g, " ").trim().toLowerCase();
}

function renderFactSetCellContent(
  cell: string,
  activeCitationId: string | null
): React.ReactNode {
  const segments = parseUnderlineTags(cell);
  return segments.map((segment, index) => {
    if (segment.type === "text") {
      return renderInlineFactSetText(segment.content, `factset-text-${index}`);
    }
    const underline = (
      <CitationUnderline
        key={`factset-citation-${index}-${segment.attributes.id}`}
        id={segment.attributes.id}
        dataSource={segment.attributes.dataSource}
        dataType={segment.attributes.dataType}
      >
        {renderInlineFactSetText(segment.content, `factset-citation-text-${index}`)}
      </CitationUnderline>
    );
    if (segment.attributes.id && segment.attributes.id === activeCitationId) {
      return (
        <span
          key={`factset-active-${index}`}
          className="factset-table-active-citation citation-html-active-target"
        >
          {underline}
        </span>
      );
    }
    return underline;
  });
}

function renderInlineFactSetText(text: string, keyPrefix: string): React.ReactNode[] {
  const out: React.ReactNode[] = [];
  const regex = /(\*\*(.+?)\*\*)|(\*(.+?)\*)|(`(.+?)`)/g;
  let lastIndex = 0;
  let key = 0;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) out.push(text.slice(lastIndex, match.index));
    if (match[1]) {
      out.push(<strong key={`${keyPrefix}-${key++}`}>{match[2]}</strong>);
    } else if (match[3]) {
      out.push(<em key={`${keyPrefix}-${key++}`}>{match[4]}</em>);
    } else if (match[5]) {
      out.push(<code key={`${keyPrefix}-${key++}`}>{match[6]}</code>);
    }
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) out.push(text.slice(lastIndex));
  return out;
}
